import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ArrowLeft, ArrowRight, Check, Info, Search, Plus } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import SurveyService from "../lib/SurveyService";
import {
  processSurveyIntoPanels,
  createSimplePanels,
  formatSurveyResponses,
  mapQuestionTypeToUIType,
} from "../utils/SurveyProcessor";

// Question types remain the same
const QUESTION_TYPES = {
  TEXT: "text",
  SEARCH: "search",
  SELECT: "select",
  BOOLEAN: "boolean",
  MULTIPLE_CHOICE: "multiple_choice",
  MULTI_SELECT: "multi_select",
  ADDRESS: "address",
  CONFIRMATION: "confirmation",
  VIN: "vin",
};

export const MOCK_US_STATES = [
  { id: "AL", text: "Alabama", value: "AL" },
  { id: "AK", text: "Alaska", value: "AK" },
  { id: "AZ", text: "Arizona", value: "AZ" },
  { id: "AR", text: "Arkansas", value: "AR" },
  { id: "CA", text: "California", value: "CA" },
  { id: "CO", text: "Colorado", value: "CO" },
  { id: "CT", text: "Connecticut", value: "CT" },
  { id: "DE", text: "Delaware", value: "DE" },
  { id: "FL", text: "Florida", value: "FL" },
  { id: "GA", text: "Georgia", value: "GA" },
  { id: "HI", text: "Hawaii", value: "HI" },
  { id: "ID", text: "Idaho", value: "ID" },
  { id: "IL", text: "Illinois", value: "IL" },
  { id: "IN", text: "Indiana", value: "IN" },
  { id: "IA", text: "Iowa", value: "IA" },
  { id: "KS", text: "Kansas", value: "KS" },
  { id: "KY", text: "Kentucky", value: "KY" },
  { id: "LA", text: "Louisiana", value: "LA" },
  { id: "ME", text: "Maine", value: "ME" },
  { id: "MD", text: "Maryland", value: "MD" },
  { id: "MA", text: "Massachusetts", value: "MA" },
  { id: "MI", text: "Michigan", value: "MI" },
  { id: "MN", text: "Minnesota", value: "MN" },
  { id: "MS", text: "Mississippi", value: "MS" },
  { id: "MO", text: "Missouri", value: "MO" },
  { id: "MT", text: "Montana", value: "MT" },
  { id: "NE", text: "Nebraska", value: "NE" },
  { id: "NV", text: "Nevada", value: "NV" },
  { id: "NH", text: "New Hampshire", value: "NH" },
  { id: "NJ", text: "New Jersey", value: "NJ" },
  { id: "NM", text: "New Mexico", value: "NM" },
  { id: "NY", text: "New York", value: "NY" },
  { id: "NC", text: "North Carolina", value: "NC" },
  { id: "ND", text: "North Dakota", value: "ND" },
  { id: "OH", text: "Ohio", value: "OH" },
  { id: "OK", text: "Oklahoma", value: "OK" },
  { id: "OR", text: "Oregon", value: "OR" },
  { id: "PA", text: "Pennsylvania", value: "PA" },
  { id: "RI", text: "Rhode Island", value: "RI" },
  { id: "SC", text: "South Carolina", value: "SC" },
  { id: "SD", text: "South Dakota", value: "SD" },
  { id: "TN", text: "Tennessee", value: "TN" },
  { id: "TX", text: "Texas", value: "TX" },
  { id: "UT", text: "Utah", value: "UT" },
  { id: "VT", text: "Vermont", value: "VT" },
  { id: "VA", text: "Virginia", value: "VA" },
  { id: "WA", text: "Washington", value: "WA" },
  { id: "WV", text: "West Virginia", value: "WV" },
  { id: "WI", text: "Wisconsin", value: "WI" },
  { id: "WY", text: "Wyoming", value: "WY" },
  { id: "DC", text: "District of Columbia", value: "DC" },
];

// Mock data for insurance companies
export const MOCK_INSURANCE_COMPANIES = [
  { id: "geico", text: "Geico", value: "geico" },
  { id: "progressive", text: "Progressive", value: "progressive" },
  { id: "statefarm", text: "State Farm", value: "statefarm" },
  { id: "allstate", text: "Allstate", value: "allstate" },
  { id: "usaa", text: "USAA", value: "usaa" },
  { id: "libertymutual", text: "Liberty Mutual", value: "libertymutual" },
  { id: "farmers", text: "Farmers", value: "farmers" },
  { id: "nationwide", text: "Nationwide", value: "nationwide" },
  { id: "amfam", text: "American Family", value: "amfam" },
  { id: "travelers", text: "Travelers", value: "travelers" },
  { id: "erie", text: "Erie Insurance", value: "erie" },
  { id: "aaa", text: "AAA", value: "aaa" },
  { id: "metlife", text: "MetLife", value: "metlife" },
  { id: "amica", text: "Amica", value: "amica" },
  { id: "country", text: "Country Financial", value: "country" },
  { id: "auto-owners", text: "Auto-Owners Insurance", value: "auto-owners" },
  { id: "mercury", text: "Mercury Insurance", value: "mercury" },
  { id: "the-general", text: "The General", value: "the-general" },
  { id: "21st", text: "21st Century", value: "21st" },
];
//  { id: "no_coverage", text: "No current coverage", value: "no_coverage" },

const PostAuthenticationSurvey = ({ onComplete, onBack, renewal }) => {
  // Auth hook to get the user and token
  const { getIdToken, user, loading: authLoading } = useAuth();

  // Survey ID - this could come from props or configuration
  //const [surveyId] = useState("STEM_PZKAVubsaW");
  const [surveyId] = useState("STEM_WPFZm8Kfn1");
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = sessionStorage.getItem("postAuthSurveyStep");
    return savedStep ? parseInt(savedStep, 10) : 0;
  });

  // All answers across all steps
  const [answers, setAnswers] = useState(() => {
    const savedAnswers = sessionStorage.getItem("postAuthSurveyData");
    return savedAnswers ? JSON.parse(savedAnswers) : {};
  });

  const [isLoading, setIsLoading] = useState(true);
  const [surveyData, setSurveyData] = useState(null);
  const [surveyPanels, setSurveyPanels] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [errors, setErrors] = useState({});

  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [placesAutocomplete, setPlacesAutocomplete] = useState(null);

  const [isReadOnly, setIsReadOnly] = useState(false);
  const [previousResponses, setPreviousResponses] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const addressInputRef = useRef(null);
  const fetchedRef = useRef(false);
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = sessionStorage.getItem("postAuthSurveyActiveTab");
    return savedTab || "DRIVER_COVERAGE";
  });
  const [showEditConfirmation, setShowEditConfirmation] = useState(false);

  useEffect(() => {
    if (!isReadOnly) {
      sessionStorage.setItem("postAuthSurveyActiveTab", activeTab);
    }
  }, [activeTab, isReadOnly]);

  const isCurrentTabValid = () => {
    if (isReadOnly) return true;

    const allQuestions = surveyData?.questions || [];
    const tabQuestions = getQuestionsForTab(activeTab, allQuestions);

    let isValid = true;

    tabQuestions.forEach((question) => {
      //if (!question.required) return;

      const answer = answers[question.id];
      if (!answer) {
        isValid = false;
        return;
      }

      // Check for "Other" value with empty text
      if (
        question.allowOther &&
        answer.value === "other" &&
        (!answer.otherAnswer || answer.otherAnswer.trim() === "")
      ) {
        isValid = false;
        return;
      }

      // Apply specific validation logic based on question type
      const uiType = mapQuestionTypeToUIType(question);

      if (uiType === "VIN" && answer.value) {
        if (answer.value.length !== 17) {
          isValid = false;
          return;
        }
      }

      // Validate CHOICE questions
      if (question.type === "CHOICE") {
        if (question.multiSelect) {
          if (!answer.values || answer.values.length === 0) {
            isValid = false;
            return;
          }
        } else {
          if (!answer.value) {
            isValid = false;
            return;
          }
        }
      }

      // Validate TEXT questions
      if (question.type === "TEXT") {
        if (!answer.value || answer.value.trim() === "") {
          isValid = false;
          return;
        }
      }
    });

    return isValid;
  };
  const isTabComplete = (tabKey) => {
    const allQuestions = surveyData?.questions || [];
    const tabQuestions = getQuestionsForTab(tabKey, allQuestions);
    // Check if ALL required questions are answered
    return tabQuestions.every((question) => {
      //if (!question.required) return true;

      const answer = answers[question.id];
      // If no answer at all
      if (!answer) {
        return false;
      }

      // Check for "Other" value with empty text
      if (
        question.allowOther &&
        answer.value === "other" &&
        (!answer.otherAnswer || answer.otherAnswer.trim() === "")
      ) {
        return false;
      }

      // Apply specific validation logic
      const uiType = mapQuestionTypeToUIType(question);

      if (uiType === "VIN" && answer.value) {
        if (answer.value.length !== 17) {
          return false;
        }
      }

      // Validate CHOICE questions
      if (question.type === "CHOICE") {
        if (question.multiSelect) {
          if (!answer.values || answer.values.length === 0) {
            return false;
          }
        } else {
          if (!answer.value) {
            return false;
          }
        }
      }

      // Validate TEXT questions
      if (question.type === "TEXT") {
        if (!answer.value || answer.value.trim() === "") {
          return false;
        }
      }

      return true;
    });
  };

  // 🔥 NEW: Get the next tab key
  const getNextTab = (currentTabKey) => {
    const tabKeys = Object.keys(TAB_GROUPS);
    const currentIndex = tabKeys.indexOf(currentTabKey);
    return currentIndex < tabKeys.length - 1 ? tabKeys[currentIndex + 1] : null;
  };

  // 🔥 NEW: Get the previous tab key
  const getPreviousTab = (currentTabKey) => {
    const tabKeys = Object.keys(TAB_GROUPS);
    const currentIndex = tabKeys.indexOf(currentTabKey);
    return currentIndex > 0 ? tabKeys[currentIndex - 1] : null;
  };

  // 🔥 NEW: Check if we're on the last tab
  const isLastTab = () => {
    const tabKeys = Object.keys(TAB_GROUPS);
    return activeTab === tabKeys[tabKeys.length - 1];
  };

  const handleTabSwitch = (newTab) => {
    // If current tab is not valid, prevent switching
    if (!isCurrentTabValid()) {
      // Optionally, you could show a toast or alert
      return;
    }

    // If validation passes, switch tabs
    setActiveTab(newTab);
  };

  // Current step in the multi-step survey

  useEffect(() => {
    const validateCurrentStep = () => {
      if (isReadOnly) {
        setIsFormValid(true);
        return;
      }

      if (!surveyData?.questions) {
        setIsFormValid(false);
        return;
      }

      const allQuestions = surveyData.questions;
      let isValid = true;

      // Check ALL questions across ALL tabs for final form validity
      allQuestions.forEach((question) => {
        //if (!question.required) return;

        const answer = answers[question.id];
        if (!answer) {
          isValid = false;
          return;
        }

        // Check for "Other" value with empty text
        if (
          question.allowOther &&
          answer.value === "other" &&
          (!answer.otherAnswer || answer.otherAnswer.trim() === "")
        ) {
          isValid = false;
          return;
        }

        // Apply specific validation logic based on question type
        const uiType = mapQuestionTypeToUIType(question);

        if (uiType === "VIN" && answer.value) {
          if (answer.value.length !== 17) {
            isValid = false;
            return;
          }
        }

        // Validate CHOICE questions
        if (question.type === "CHOICE") {
          if (question.multiSelect) {
            // Multi-select: check if values array exists and has items
            if (!answer.values || answer.values.length === 0) {
              isValid = false;
              return;
            }
          } else {
            // Single-select: check if value exists
            if (!answer.value) {
              isValid = false;
              return;
            }
          }
        }

        // Validate TEXT questions
        if (question.type === "TEXT") {
          if (!answer.value || answer.value.trim() === "") {
            isValid = false;
            return;
          }
        }
      });

      setIsFormValid(isValid);
    };

    validateCurrentStep();
  }, [answers, isReadOnly, surveyData]);

  // Fetch survey data and check for previous responses
  useEffect(() => {
    if (!authLoading && user) {
      const fetchSurveyAndResponses = async () => {
        if (fetchedRef.current) return; // Skip if already fetched

        try {
          setIsLoading(true);
          fetchedRef.current = true; // Mark as fetched
          // Get Firebase token
          let token = null;
          try {
            token = await getIdToken();
          } catch (e) {
            console.error("Error getting token:", e);
          }

          // 1. First, check if user has already completed this survey
          let responseData;
          try {
            responseData = await SurveyService.getSurveyResponsesBySurveyId(
              surveyId,
              token
            );
            if (responseData && responseData.dateCompleted) {
              // User has completed this survey before
              setPreviousResponses(responseData);
              setIsReadOnly(true);
              console.log(
                "Survey previously completed. Displaying in read-only mode."
              );
            }
          } catch (err) {
            console.log("No previous responses found or error checking:", err);
            // Continue with normal survey flow if no previous responses
          }

          // 2. Fetch survey data (template) regardless
          const survey = await SurveyService.getSurveyById(surveyId, token);
          setSurveyData(survey);
          // Process survey into panels
          const panels = createSimplePanels(survey);
          setSurveyPanels(panels);
          if (currentStep > panels.length - 1) {
            setCurrentStep(0);
          }
          // If we have previous responses, now we can properly map them
          if (responseData && responseData.dateCompleted && survey) {
            const convertedAnswers = convertResponsesFormatToAnswers(
              responseData,
              survey
            );
            setAnswers(convertedAnswers);
          }
          setIsLoading(false);
        } catch (err) {
          console.error("Error loading survey:", err);
          setErrors((prev) => ({
            ...prev,
            general: "Failed to load survey questions. Please try again.",
          }));
          setIsLoading(false);
        }
      };

      fetchSurveyAndResponses();
    } else if (!authLoading && !user) {
      // Auth finished loading but no user - handle accordingly
      setIsLoading(false);
    }
  }, [authLoading, surveyId]);

  // Save current step to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("postAuthSurveyStep", currentStep.toString());
  }, [currentStep]);

  // Save answers to sessionStorage whenever they change
  useEffect(() => {
    if (Object.keys(answers).length > 0 && !isReadOnly) {
      sessionStorage.setItem("postAuthSurveyData", JSON.stringify(answers));
    }
  }, [answers, isReadOnly]);

  // Function to convert server response format to component answers format
  const convertResponsesFormatToAnswers = (responseData, surveyTemplate) => {
    if (!responseData || !responseData.responses || !surveyTemplate) {
      return {};
    }

    const convertedAnswers = {};

    responseData.responses.forEach((response) => {
      // Find the corresponding question in the survey template
      const question = surveyTemplate.questions.find(
        (q) => q.id === response.questionId
      );
      if (!question) return;

      const questionId = response.questionId;

      switch (response.responseType) {
        case "TEXT":
          convertedAnswers[questionId] = {
            value: response.answer || "",
          };
          break;

        case "CHOICE":
          if (question.multiSelect) {
            // Multi-select question
            const values = response.selections.map((s) => s.optionValue);
            const answerIds = response.selections.map((s) => s.id);

            convertedAnswers[questionId] = {
              values,
              answerIds,
            };
          } else {
            // Single-select question
            if (response.selections && response.selections.length > 0) {
              const answer = {
                value: response.selections[0].optionValue,
                answerId: response.selections[0].id,
              };
              // Add otherAnswer if it exists in the response
              if (response.otherAnswer) {
                answer.otherAnswer = response.otherAnswer;
              }
              convertedAnswers[questionId] = answer;
            }
          }
          break;

        default:
          console.warn(`Unsupported response type: ${response.responseType}`);
      }
    });

    return convertedAnswers;
  };

  const getCurrentStep = () => surveyPanels[currentStep] || null;

  const validateCurrentStep = () => {
    if (isReadOnly) return true;

    const step = getCurrentStep();
    if (!step) return false;

    let isValid = true;
    const newErrors = { ...errors };

    // Check each question in the step
    step.questions.forEach((question) => {
      //if (!question.required) return;

      const answer = answers[question.id];
      if (!answer) {
        newErrors[question.id] = `This field is required`;
        isValid = false;
        return;
      }

      // Check for "Other" value with empty text
      if (
        question.allowOther &&
        answer.value === "other" &&
        (!answer.otherAnswer || answer.otherAnswer.trim() === "")
      ) {
        newErrors[question.id] = "Please specify the other option";
        isValid = false;
        return;
      }

      // Apply any specific validation logic based on question type
      const uiType = mapQuestionTypeToUIType(question);

      if (uiType === "VIN" && answer.value) {
        if (answer.value.length !== 17) {
          newErrors[question.id] = "VIN must be exactly 17 characters";
          isValid = false;
        }
      }

      // Add other validation rules as needed
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSingleAnswer = (questionId, value, answerId) => {
    if (isReadOnly) return;

    setErrors({ ...errors, [questionId]: null });
    // Preserve otherAnswer if it exists in the current answer
    const currentOtherAnswer = answers[questionId]?.otherAnswer || "";
    const newAnswers = {
      ...answers,
      [questionId]: {
        value,
        answerId,
        ...(currentOtherAnswer ? { otherAnswer: currentOtherAnswer } : {}),
      },
    };

    setAnswers(newAnswers);
  };

  const handleTextAnswer = (questionId, value) => {
    if (isReadOnly) return;

    setErrors({ ...errors, [questionId]: null });

    const newAnswers = {
      ...answers,
      [questionId]: {
        value,
      },
    };

    setAnswers(newAnswers);
  };

  const handleAddressSelect = (questionId, value) => {
    if (isReadOnly) return;

    setErrors({ ...errors, [questionId]: null });
    setSearchQuery(value);
    setAddressSuggestions([]);

    const newAnswers = {
      ...answers,
      [questionId]: {
        value,
      },
    };

    setAnswers(newAnswers);
  };

  const handleMultipleAnswer = (questionId, value, answerId, isChecked) => {
    if (isReadOnly) return;

    setErrors({ ...errors, [questionId]: null });

    // Initialize array if not exists
    const currentValues = answers[questionId]?.values || [];
    const currentIds = answers[questionId]?.answerIds || [];

    let newValues, newIds;

    if (isChecked) {
      // Add value if checked
      newValues = [...currentValues, value];
      newIds = [...currentIds, answerId];
    } else {
      // Remove value if unchecked
      newValues = currentValues.filter((v) => v !== value);
      newIds = currentIds.filter((id) => id !== answerId);
    }

    const newAnswers = {
      ...answers,
      [questionId]: {
        values: newValues,
        answerIds: newIds,
      },
    };

    setAnswers(newAnswers);
  };

  // Add this at the top of PostAuthenticationSurvey.js
  const TAB_GROUPS = {
    DRIVER_COVERAGE: {
      label: "Driver & Coverage",
      icon: "👤", // Optional: Add icons for visual appeal
      description: "Tell us about your insurance history",
      questionIds: [
        "SQUE_M6Yemn9f2s", // Homeowner
        "SQUE_mAEfOvCc8G", // DUI/SR-22
        "SQUE_wIvGbUHtdb", // Insurance in last 30 days
        "SQUE_ZedgNGz9wI", // Current insurer -         "SQUE_bHb4gFrACM" for staging
        "SQUE_C764CxHIN0", // Bodily injury limits
      ],
      // Fallback: use displayOrder if IDs change
      displayOrderRange: [0, 4], // inclusive
    },
    VEHICLE_DETAILS: {
      label: "Vehicle Details",
      icon: "🚗",
      description: "Information about your vehicle",
      questionIds: [
        "SQUE_4vJMNFbLFy", // Driver Performance Monitoring
        "SQUE_S99VhmpIMS", // Car age
        "SQUE_aylTEZrHqY", // Is EV
      ],
      displayOrderRange: [5, 7], // inclusive
    },
  };

  // Helper function to get questions for a tab (with fallback)
  const getQuestionsForTab = (tabKey, allQuestions) => {
    const tabConfig = TAB_GROUPS[tabKey];

    // Primary: Filter by question IDs
    let filtered = allQuestions.filter((q) =>
      tabConfig.questionIds.includes(q.id)
    );

    // Fallback: If no matches, use displayOrder range
    if (filtered.length === 0 && tabConfig.displayOrderRange) {
      const [min, max] = tabConfig.displayOrderRange;
      filtered = allQuestions.filter(
        (q) => q.displayOrder >= min && q.displayOrder <= max
      );
    }

    // Sort by displayOrder to maintain intended sequence
    return filtered.sort((a, b) => a.displayOrder - b.displayOrder);
  };

  const handleComplete = async () => {
    if (isReadOnly) {
      if (onComplete) {
        onComplete({
          answers: previousResponses.responses,
          completedAt: previousResponses.dateCompleted,
          readOnly: true,
        });
      }
      return;
    }
    try {
      setIsLoading(true);

      // Validate all questions to ensure completeness
      if (!validateCurrentStep()) {
        setIsLoading(false);
        return;
      }

      // Get Firebase token
      const token = await getIdToken();

      // Format answers for API submission
      const formattedResponses = formatSurveyResponses(
        answers,
        surveyData.questions
      );

      // Submit survey responses
      await SurveyService.submitSurveyResponses(
        surveyId,
        user.uid,
        formattedResponses,
        token
      );

      // Call onComplete with ONLY the answers, not the full steps (which contain functions)
      if (onComplete) {
        sessionStorage.removeItem("postAuthSurveyActiveTab");

        onComplete({
          answers: formattedResponses,
          completedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Error submitting survey:", error);
      setErrors((prev) => ({
        ...prev,
        general: "Failed to submit survey. Please try again.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepQuestions = () => {
    const allQuestions = surveyData?.questions || [];
    if (!allQuestions.length) return null;

    // 🔥 MODIFIED: Filter questions based on active tab
    const questionsToShow = isReadOnly
      ? allQuestions // Show all questions in read-only mode
      : getQuestionsForTab(activeTab, allQuestions);

    return (
      <div className="space-y-6">
        {questionsToShow.map((question, displayIndex) => {
          const questionError = errors[question.id];
          const uiType = mapQuestionTypeToUIType(question);

          // 🔥 MODIFIED: Use actual question index in all questions array
          const actualIndex = allQuestions.findIndex(
            (q) => q.id === question.id
          );

          return (
            <div key={question.id} className="space-y-2">
              <div className="flex items-start gap-2">
                <label htmlFor={question.id} className="text-base font-bold">
                  {question.prompt}
                </label>
              </div>

              {/* Render appropriate input based on question type */}
              {renderQuestionInput(question, uiType)}

              {questionError && (
                <p className="text-red-500 text-sm">{questionError}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render the appropriate input based on question type
  const renderQuestionInput = (question, uiType) => {
    const questionError = errors[question.id];
    if (question.choiceQuestionType === "US_STATES") {
      return (
        <select
          id={question.id}
          value={answers[question.id]?.value || ""}
          onChange={(e) => {
            const option = MOCK_US_STATES.find(
              (state) => state.value === e.target.value
            );
            if (option) {
              handleSingleAnswer(question.id, option.value, option.id);
            }
          }}
          className={`w-full p-3 border rounded-lg focus:outline-none ${
            isReadOnly
              ? "bg-gray-50 text-gray-700"
              : "focus:ring-2 focus:ring-blue-500"
          } ${questionError ? "border-red-500" : "border-gray-300"}`}
          disabled={isReadOnly}
        >
          <option value="">Select state...</option>
          {MOCK_US_STATES.map((state) => (
            <option key={state.id} value={state.value}>
              {state.text}
            </option>
          ))}
        </select>
      );
    }

    // Special handling for INSURANCE_PROVIDERS questions
    if (question.choiceQuestionType === "INSURANCE_PROVIDERS") {
      return (
        <div className="space-y-2">
          <div className="relative !mt-3">
            <select
              id={question.id}
              value={answers[question.id]?.value || ""}
              onChange={(e) => {
                const selectedValue = e.target.value;
                if (selectedValue === "other") {
                  setAnswers((prev) => ({
                    ...prev,
                    [question.id]: {
                      value: "other",
                      answerId: "COPT_other",
                      otherAnswer: prev[question.id]?.otherAnswer || "",
                    },
                  }));
                } else if (selectedValue === "no_coverage") {
                  handleSingleAnswer(question.id, selectedValue, selectedValue);
                } else {
                  const option = MOCK_INSURANCE_COMPANIES.find(
                    (company) => company.value === e.target.value
                  );
                  if (option) {
                    handleSingleAnswer(question.id, option.value, option.id);
                  }
                }
              }}
              className={`w-full text-darkGrey pl-3 pr-10 py-3 text-base rounded-[10px] border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                isReadOnly ? "bg-gray-50 cursor-not-allowed" : ""
              } ${
                questionError ? "border-red-500" : "border-gray-300"
              } appearance-none`}
              disabled={isReadOnly}
            >
              <option value="" disabled className="text-gray-400">
                Select insurance company...
              </option>
              {[...MOCK_INSURANCE_COMPANIES]
                .sort((a, b) => a.text.localeCompare(b.text))
                .map((company) => (
                  <option key={company.id} value={company.value}>
                    {company.text}
                  </option>
                ))}
              <option key="no_coverage" value="no_coverage">
                No current coverage
              </option>
              {question.allowOther && (
                <option value="other">
                  {question.otherOptionLabel || "Other"}
                </option>
              )}
            </select>
            {/* Chevron icon */}
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-500"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
          </div>
          {/* Show text input when "Other" is selected */}
          {question.allowOther && answers[question.id]?.value === "other" && (
            <input
              type="text"
              value={answers[question.id]?.otherAnswer || ""}
              onChange={(e) => {
                // Update the otherAnswer property while keeping the value as "other"
                setAnswers((prev) => ({
                  ...prev,
                  [question.id]: {
                    ...prev[question.id],
                    otherAnswer: e.target.value,
                  },
                }));
              }}
              placeholder="Please specify"
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                questionError ? "border-red-500" : "border-gray-300"
              }`}
              disabled={isReadOnly}
            />
          )}
        </div>
      );
    }
    switch (uiType) {
      case "TEXT":
        return (
          <input
            id={question.id}
            type="text"
            value={answers[question.id]?.value || ""}
            onChange={(e) => handleTextAnswer(question.id, e.target.value)}
            placeholder={question.placeholderText || ""}
            maxLength={question.maxLength}
            disabled={isReadOnly}
            className={`w-full p-3 border rounded-lg focus:outline-none ${
              isReadOnly
                ? "bg-gray-50 text-gray-700"
                : "focus:ring-2 focus:ring-blue-500"
            } ${questionError ? "border-red-500" : "border-gray-300"}`}
          />
        );

      case "ADDRESS":
        return (
          <div className="relative">
            <input
              id={question.id}
              ref={addressInputRef}
              type="text"
              value={answers[question.id]?.value || ""}
              onChange={(e) => {
                handleTextAnswer(question.id, e.target.value);
              }}
              placeholder={
                question.placeholderText || "Start typing your address"
              }
              disabled={isReadOnly}
              className={`w-full p-3 border rounded-lg focus:outline-none ${
                isReadOnly
                  ? "bg-gray-50 text-gray-700"
                  : "focus:ring-2 focus:ring-blue-500"
              } ${questionError ? "border-red-500" : "border-gray-300"}`}
            />
            {!googleMapsLoaded && (
              <div className="text-xs text-gray-500 mt-1">
                Loading Google Maps...
              </div>
            )}
          </div>
        );

      case "VIN":
        return (
          <div className="space-y-4">
            <input
              id={question.id}
              type="text"
              value={answers[question.id]?.value || ""}
              onChange={(e) =>
                handleTextAnswer(question.id, e.target.value.toUpperCase())
              }
              placeholder={question.placeholderText || "Enter your VIN"}
              disabled={isReadOnly}
              className={`w-full p-3 border rounded-lg focus:outline-none ${
                isReadOnly
                  ? "bg-gray-50 text-gray-700"
                  : "focus:ring-2 focus:ring-blue-500"
              } ${questionError ? "border-red-500" : "border-gray-300"}`}
            />

            {/* <div className="flex items-center justify-center">
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-500">
                  Don't know your VIN?
                </span>
                <button className="text-blue-600 flex items-center gap-1 text-sm">
                  <Search className="w-4 h-4" />
                  Search for Vehicle
                </button>
              </div>
            </div> */}
          </div>
        );

      case "SELECT":
        return (
          <div className="relative !mt-3">
            <select
              id={question.id}
              value={answers[question.id]?.value || ""}
              onChange={(e) => {
                const option = question.options.find(
                  (opt) => opt.value === e.target.value
                );
                if (option) {
                  handleSingleAnswer(question.id, option.value, option.id);
                }
              }}
              className={`w-full text-darkGrey pl-3 pr-10 py-3 text-base rounded-[10px] border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                isReadOnly ? "bg-gray-50 cursor-not-allowed" : ""
              } ${
                questionError ? "border-red-500" : "border-gray-300"
              } appearance-none`}
              disabled={isReadOnly}
            >
              <option value="" disabled className="text-gray-400">
                {question.placeholder || "Select..."}
              </option>
              {question.options
                ?.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                ?.map((option) => (
                  <option key={option.id} value={option.value}>
                    {option.text}
                  </option>
                ))}
            </select>
            {/* Chevron icon */}
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-500"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
          </div>
        );

      case "BOOLEAN":
      case "MULTIPLE_CHOICE":
        return (
          <div className="space-y-3">
            {question.options?.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() =>
                  handleSingleAnswer(question.id, option.value, option.id)
                }
                className={`w-full p-4 text-left rounded-lg border transition-all ${
                  answers[question.id]?.value === option.value
                    ? "border-blue-600 bg-blue-50"
                    : "border-gray-200 hover:border-blue-300"
                }`}
              >
                {option.text}
              </button>
            ))}
          </div>
        );

      case "MULTI_SELECT":
        return (
          <div className="space-y-3">
            {question.options?.map((option) => {
              const isChecked =
                answers[question.id]?.values?.includes(option.value) || false;

              return (
                <label
                  key={option.id}
                  className={`flex items-center gap-2 p-4 rounded-lg border transition-all cursor-pointer ${
                    isChecked
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) =>
                      handleMultipleAnswer(
                        question.id,
                        option.value,
                        option.id,
                        e.target.checked
                      )
                    }
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span>{option.text}</span>
                </label>
              );
            })}
          </div>
        );

      case "CONFIRMATION":
        return (
          <label className="flex items-start gap-2 p-4 rounded-lg border transition-all cursor-pointer hover:border-blue-300">
            <input
              type="checkbox"
              checked={answers[question.id]?.value === "confirmed"}
              onChange={(e) =>
                handleSingleAnswer(
                  question.id,
                  e.target.checked ? "confirmed" : "",
                  e.target.checked ? "a_confirmed" : ""
                )
              }
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mt-1"
            />
            <span>
              {question.confirmationText ||
                "I confirm this information is correct"}
            </span>
          </label>
        );

      default:
        return <div>Unsupported question type: {uiType}</div>;
    }
  };

  if (isLoading && !surveyPanels.length) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        <span className="ml-3">Synchronizing survey data...</span>
      </div>
    );
  }

  if (errors.general) {
    return (
      <div className="bg-red-50 border border-red-200 p-4 rounded-lg text-red-800">
        <h3 className="font-medium">Error</h3>
        <p>{errors.general}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-red-600 hover:text-red-800"
        >
          Retry
        </button>
      </div>
    );
  }

  const step = getCurrentStep();

  if (!step) {
    return <div>No survey questions available</div>;
  }

  // Calculate progress as percentage of steps
  const progress = (currentStep / (surveyPanels.length - 1)) * 100;

  return (
    <div className="p-4 space-y-6">
      <div className="text-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Confirm Your Insurance Details
        </h1>
        <p className="text-gray-600">
          We use this information to match you with the best options to save
          before your renewal.
        </p>
      </div>
      {/* Read-only banner */}
      {isReadOnly && renewal && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <Info className="h-5 w-5 text-blue-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                This survey was completed on{" "}
                {new Date(previousResponses.dateCompleted).toLocaleDateString()}
                .{" "}
                {isReadOnly ? (
                  <button
                    onClick={() => {
                      setShowEditConfirmation(true);

                      // const confirm = window.confirm(
                      //   "Are you sure you want to edit your previous survey answers?"
                      // );
                      // if (confirm) {
                      //   setIsReadOnly(false);
                      // }
                    }}
                    className="text-xs px-3 py-1 mt-2 bg-purpleGradient text-white rounded hover:bg-purpleGradientDark transition-colors"
                  >
                    Edit Answers
                  </button>
                ) : (
                  // Only show reset if answers have been modified
                  JSON.stringify(answers) !==
                    JSON.stringify(previousResponses.responses) && (
                    <button
                      onClick={() => {
                        // Reset to original responses
                        setAnswers(previousResponses.responses);
                        setIsReadOnly(true);
                      }}
                      className="text-xs px-3 py-1 mt-2 bg-purpleGradient text-white rounded hover:bg-purpleGradientDark transition-colors"
                    >
                      Reset to Original
                    </button>
                  )
                )}
              </p>
            </div>
          </div>
        </div>
      )}
      {isReadOnly && !renewal && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <Info className="h-5 w-5 text-blue-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                This survey was completed on{" "}
                {new Date(previousResponses.dateCompleted).toLocaleDateString()}
                . You can review your answers but cannot modify them.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Progress Bar */}
      {/* <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
        <div
          className="bg-blue-600 h-full transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div> */}
      {/* Step indicator */}
      {/* <div className="flex justify-between text-sm text-gray-600 px-1">
        <span>
          Step {currentStep + 1} of {surveyPanels.length}
        </span>
        <span>{Math.round(progress)}% Complete</span>
      </div> */}
      {/* Step Header */}
      {/* <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900">{step.title}</h2>
        <p className="text-gray-600 text-sm mt-1">{step.description}</p>
      </div> */}
      {/* Question Card */}
      {/* <Card>
        <CardContent className="pt-6">{renderStepQuestions()}</CardContent>
      </Card> */}
      {/* 🔥 NEW: Tab Navigation - only show if not read-only */}
      {!isReadOnly && (
        <div className="flex border-b border-gray-200 mb-6">
          {Object.entries(TAB_GROUPS).map(([key, config]) => (
            <button
              key={key}
              onClick={() => handleTabSwitch(key)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === key
                  ? "text-violet-600 border-b-2 border-violet-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                {config.icon && <span>{config.icon}</span>}
                <span>{config.label}</span>
                {/* Show checkmark if tab is complete */}
                {isTabComplete(key) && activeTab !== key && (
                  <Check className="w-4 h-4 text-green-500" />
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      {renderStepQuestions()}
      <div className="flex flex-col gap-3">
        {!isReadOnly && !isLastTab() ? (
          // Show "Next Section" button if not on last tab
          <button
            onClick={() => {
              const nextTab = getNextTab(activeTab);
              if (nextTab) {
                handleTabSwitch(nextTab);
              }
            }}
            disabled={!isCurrentTabValid()}
            className={`w-full flex items-center justify-center gap-2 uppercase font-bold px-4 py-3 rounded-[10px] transition-colors ${
              isCurrentTabValid()
                ? "bg-purpleGradient text-white hover:bg-purpleGradientDark"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            Continue to {TAB_GROUPS[getNextTab(activeTab)]?.label}
          </button>
        ) : (
          // Show submit button on last tab or in read-only mode
          <button
            onClick={handleComplete}
            disabled={isLoading || (!isReadOnly && !isFormValid)}
            className={`w-full flex items-center justify-center gap-2 uppercase font-bold px-4 py-3 rounded-[10px] transition-colors ${
              isLoading || (!isReadOnly && !isFormValid)
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-purpleGradient text-white hover:bg-purpleGradientDark"
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Processing...</span>
              </>
            ) : (
              <span>Next</span>
            )}
          </button>
        )}

        {/* Back button */}
        {!isReadOnly && activeTab !== Object.keys(TAB_GROUPS)[0] ? (
          // If not on first tab, go to previous tab
          <button
            onClick={() => {
              const prevTab = getPreviousTab(activeTab);
              if (prevTab) setActiveTab(prevTab);
            }}
            className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors"
          >
            Back to {TAB_GROUPS[getPreviousTab(activeTab)]?.label}
          </button>
        ) : !isReadOnly ? (
          // If on first tab, use onBack prop
          <button
            onClick={onBack}
            className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors"
          >
            Back
          </button>
        ) : null}
        {isReadOnly && (
          <button
            onClick={onBack}
            className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors"
          >
            Back
          </button>
        )}
      </div>

      {showEditConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h2 className="text-xl font-bold mb-4">Edit Survey Answers</h2>
            <p className="mb-4">
              Are you sure you want to edit your previous survey answers?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowEditConfirmation(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setIsReadOnly(false);
                  setShowEditConfirmation(false);
                }}
                className="px-4 py-2 bg-purpleGradient text-white rounded hover:bg-purpleGradientDark"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostAuthenticationSurvey;
